/**
 * The content strategy matrix on the Strategy Hub: what the planner and Harry steer by when they choose what to write (pillars with a
 * weight, competitors to watch, topics to avoid, a focus with an end date, the share of new pieces that must be original).
 * Data: GET / PUT /api/workspaces/:wsId/content-matrix (server/content-matrix.ts). The same matrix can be changed in Draper's chat
 * ("focus on nonprofit funding until Nov 30", "add real estate holdcos as a pillar").
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Compass, Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

interface Pillar { name: string; weight: number; notes?: string }
interface Matrix {
  pillars: Pillar[]; competitors: string[]; avoid: string[];
  focus: { text: string; until: string | null } | null; originalShare: number;
  updatedAt: string | null; updatedBy: string | null; activeFocus?: { text: string; until: string | null } | null;
}
const EMPTY: Matrix = { pillars: [], competitors: [], avoid: [], focus: null, originalShare: 0, updatedAt: null, updatedBy: null };

function Chips({ items, onRemove, testId }: { items: string[]; onRemove: (i: number) => void; testId: string }) {
  if (!items.length) return <p className="text-xs text-muted-foreground">None yet.</p>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {items.map((x, i) => (
        <span key={`${x}-${i}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs">
          {x}
          <button type="button" onClick={() => onRemove(i)} className="text-muted-foreground hover:text-foreground" aria-label={`Remove ${x}`}><X size={12} /></button>
        </span>
      ))}
    </div>
  );
}

function AddRow({ placeholder, onAdd, testId }: { placeholder: string; onAdd: (v: string) => void; testId: string }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onAdd(v.trim()); setV(""); } };
  return (
    <div className="flex gap-2">
      <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="h-8 text-sm" data-testid={testId} />
      <Button type="button" size="sm" variant="outline" onClick={add} disabled={!v.trim()}><Plus size={14} /></Button>
    </div>
  );
}

export default function ContentMatrixCard({ workspaceId }: { workspaceId: number | null }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const key = ["/api/workspaces", workspaceId, "content-matrix"];
  const { data, isLoading } = useQuery<Matrix>({
    queryKey: key,
    enabled: !!workspaceId,
    queryFn: async () => {
      const r = await fetch(`/api/workspaces/${workspaceId}/content-matrix`, { credentials: "include" });
      if (!r.ok) throw new Error("Couldn't load the content matrix");
      return r.json();
    },
  });
  const [m, setM] = useState<Matrix>(EMPTY);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (data) { setM(data); setDirty(false); } }, [data]);
  const change = (next: Matrix) => { setM(next); setDirty(true); };

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/workspaces/${workspaceId}/content-matrix`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(m) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.message || "Couldn't save");
      return j;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast({ title: "Content matrix saved", description: "The next content plan and Harry's next brief use it." }); },
    onError: (e: any) => toast({ title: "Not saved", description: e?.message, variant: "destructive" }),
  });

  if (!workspaceId) return null;
  return (
    <div className="rounded-2xl border border-border bg-card/50 p-5 space-y-5" data-testid="content-matrix-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Compass size={16} className="text-violet-400" /><h3 className="text-sm font-display font-bold text-foreground">Content matrix</h3></div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">What Draper's planner and Harry steer by when they choose the next blogs and posts. It shapes the next plan, not one already approved. You can also tell Draper in chat, for example "focus on nonprofit funding until Nov 30".</p>
        </div>
        <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="button-save-matrix">
          {save.isPending ? <Loader2 className="animate-spin" /> : <Check />} Save
        </Button>
      </div>
      {isLoading ? <Loader2 className="animate-spin text-muted-foreground" /> : (
        <>
          <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Focus</div>
            <div className="flex flex-wrap gap-2">
              <Input value={m.focus?.text ?? ""} onChange={(e) => change({ ...m, focus: e.target.value ? { text: e.target.value, until: m.focus?.until ?? null } : null })} placeholder="e.g. Year-end planning for owner-managers" className="h-8 text-sm flex-1 min-w-[14rem]" data-testid="input-matrix-focus" />
              <Input type="date" value={m.focus?.until ?? ""} onChange={(e) => change({ ...m, focus: m.focus ? { ...m.focus, until: e.target.value || null } : null })} className="h-8 text-sm w-40" disabled={!m.focus} data-testid="input-matrix-focus-until" />
            </div>
            <p className="text-xs text-muted-foreground">At least half of the new pieces in the period serve it. Leave empty to follow the month's usual theme.</p>
          </section>

          <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Pillars (weight 1 to 5)</div>
            {m.pillars.length ? m.pillars.map((p, i) => (
              <div key={`${p.name}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`matrix-pillar-${i}`}>
                <span className="flex-1 min-w-0 truncate">{p.name}</span>
                <select value={p.weight} onChange={(e) => change({ ...m, pillars: m.pillars.map((x, j) => (j === i ? { ...x, weight: Number(e.target.value) } : x)) })} className="h-8 rounded-md border border-border bg-background px-2 text-sm">
                  {[1, 2, 3, 4, 5].map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
                <button type="button" onClick={() => change({ ...m, pillars: m.pillars.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-foreground" aria-label={`Remove ${p.name}`}><X size={14} /></button>
              </div>
            )) : <p className="text-xs text-muted-foreground">None yet.</p>}
            <AddRow placeholder="Add a pillar, e.g. Nonprofits and audits (ONCA, LPA)" onAdd={(v) => change({ ...m, pillars: [...m.pillars, { name: v, weight: 3 }] })} testId="input-matrix-pillar" />
            <p className="text-xs text-muted-foreground">A topic in a weight-5 pillar ranks well above the same topic with no pillar.</p>
          </section>

          <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Competitors to watch</div>
            <Chips items={m.competitors} onRemove={(i) => change({ ...m, competitors: m.competitors.filter((_, j) => j !== i) })} testId="matrix-competitors" />
            <AddRow placeholder="Add a competitor (name or blog URL)" onAdd={(v) => change({ ...m, competitors: [...m.competitors, v] })} testId="input-matrix-competitor" />
          </section>

          <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Never write about</div>
            <Chips items={m.avoid} onRemove={(i) => change({ ...m, avoid: m.avoid.filter((_, j) => j !== i) })} testId="matrix-avoid" />
            <AddRow placeholder="Add a topic or kind of piece to avoid" onAdd={(v) => change({ ...m, avoid: [...m.avoid, v] })} testId="input-matrix-avoid" />
          </section>

          <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Original pieces</div>
            <div className="flex items-center gap-2 text-sm">
              <Input type="number" min={0} max={100} value={m.originalShare} onChange={(e) => change({ ...m, originalShare: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} className="h-8 w-20 text-sm" data-testid="input-matrix-original" />
              <span className="text-muted-foreground text-xs">% of new pieces must be original: a guide, map, checklist or report no Canadian firm has published.</span>
            </div>
          </section>
          {m.updatedAt && <p className="text-[11px] text-muted-foreground">Last changed {new Date(m.updatedAt).toLocaleString()} by {m.updatedBy}.</p>}
        </>
      )}
    </div>
  );
}
